import Cookie from "/app/utils/cookie";
import * as Sentry from '@sentry/vue';
import { isNavigationFailure } from 'vue-router';
import { get } from 'lodash';
import { version } from '../../package.json';
import { formatComponentName, generateComponentTrace } from '/app/utils/component';
import { config } from '/app/config';
import { defineAppPlugin } from '/app/utils/app';
import { canRecoveryReload } from '/app/utils/navigation';
import { isIgnoredAppUpdatePath } from '/app/utils/app-update';

const ignoredStatus = [
  401, // Unauthorized 
  403, // Forbidden
  404, // Not Found
  412, // Precondition Failed
  422, // Unprocessable Entity
  429, // Too Many Requests
  504, // Gateway Timeout
];

const stringify = (value) => {
  if (typeof value === 'object') {
    return JSON.stringify(value, null, 2);
  }
  return value || 'no';
};

const resolveMetadata = (vm, options = {}) => {
  if (!vm) {
    return {};
  }

  const { attachProps, attachAttrs } = options;
  const trace = generateComponentTrace(vm);
  const component = formatComponentName(vm, true);
  const metadata = { component, trace };

  if (attachProps) {
    metadata.props = stringify(vm.$props);
  }

  if (attachAttrs) {
    metadata.attrs = stringify(vm.$attrs);
  }

  // adds extra info to the error
  const context = vm._;
  if (context) {
    metadata.context = stringify({
      name: get(context, 'type.name', get(context, 'type.title', 'Anonymous')),
      file: context.__file || get(vm, '$options.__file'),
      route: vm.$route ? {
        path: vm.$route.path,
        name: vm.$route.name,
        params: vm.$route.params,
        query: vm.$route.query
      } : undefined,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
    });
  }

  return metadata;
}

const useErrorHandler = (app, options = {}) => {
  const { errorHandler } = options;

  app.config.errorHandler = (err, vm, lifecycleHook) => {
    const component = resolveMetadata(vm, options);
    const parent = vm.$parent ? resolveMetadata(vm.$parent, options) : {};

    Sentry.withScope(scope => {
      scope.setContext('Component', {
        lifecycleHook, ...component,
      });

      scope.setContext('Parent...', parent);
      scope.setContext('Options', options);

      Sentry.captureException(err);
    })

    if (typeof errorHandler === 'function') {
      errorHandler.call(app, err, vm, lifecycleHook);
    }
  }
}

export const sentry = Sentry;

/**
 * Reports an error that made a page bail out to a fallback route (e.g. the
 * send-feedback page). The original error is often an HTTP failure whose
 * message ("... status code 403") matches `ignoreErrors`, so it is wrapped in
 * a dedicated error that keeps the response details as context.
 */
export const captureRedirectError = (reason, err, extra = {}) => {
  const response = err?.response;
  const status = response?.status;

  const context = {
    ...extra,
    status,
    method: err?.config?.method,
    url: err?.config?.url,
    response: stringify(response?.data)?.slice?.(0, 2000),
    message: err?.message,
    name: err?.name,
    stack: err?.stack?.split('\n').slice(0, 15).join('\n'),
  };

  console.group(`[${reason}] redirecting to fallback (status=${status ?? 'none'})`);
  console.error('Error:', err);
  console.error('Response:', response?.data);
  console.error('Context:', context);
  console.groupEnd();

  Sentry.withScope(scope => {
    scope.setLevel('error');
    scope.setTag('redirect_reason', reason);
    scope.setTag('http_status', status ?? 'none');
    scope.setFingerprint([reason, String(status ?? err?.name ?? 'unknown')]);
    scope.setContext('Redirect', context);

    const error = new Error(`[${reason}] status=${status ?? 'none'}: ${err?.message ?? err}`);
    error.cause = err;

    Sentry.captureException(error);
  });
};


const handleUnhandledRejection = (event) => {
  const reason = event.reason;

  // Skip Vue Router navigation failures (expected behavior from guards returning false)
  if (isNavigationFailure(reason)) {
    event.preventDefault();
    return;
  }

  // Skip DOM Event objects (e.g. script/CSS load failures from stale assets)
  // These are not actionable errors — they occur when users have cached pages
  // referencing old chunk URLs after a deployment
  if (reason instanceof Event || (reason && typeof reason === 'object' && 'isTrusted' in reason && 'target' in reason)) {
    event.preventDefault();
    return;
  }

  // Skip rejections thrown by browser extensions / injected scripts.
  // Safari masks their source URL as webkit-masked-url://hidden and the reason is
  // an error-like object we can't act on (e.g. "undefined is not an object
  // (evaluating 'e.useCache')"). Not our code, not actionable.
  const reasonStack = (reason && typeof reason === 'object' && typeof reason.stack === 'string')
    ? reason.stack
    : '';
  if (/webkit-masked-url|chrome-extension:\/\/|moz-extension:\/\/|safari-(web-)?extension:\/\//i.test(reasonStack)) {
    event.preventDefault();
    return;
  }

  console.group('[app] Unhandled Promise Rejection');
  console.error('Event:', event);

  const reasonObj = reason || {};
  const message = reasonObj.message || (typeof reason === 'string' ? reason : 'Unknown rejection');
  const stack = reasonObj.stack ? reasonObj.stack.split('\n').slice(0, 15).join('\n') : undefined;

  Sentry.withScope(scope => {
    scope.setLevel('error');
    scope.setTag('unhandledrejection', 'true');
    scope.setContext('PromiseRejection', {
      Code: reasonObj.code,
      Data: reasonObj.data,
      Message: message,
      Name: reasonObj.name,
      Stack: stack,
      EventType: event.type,
    });

    const error = reason instanceof Error
      ? reason
      : new Error(`[UnhandledRejection] ${message}`);

    Sentry.captureException(error);
  });

  event.preventDefault();
  console.groupEnd();
};

export default defineAppPlugin(({ app, router }) => {
  if (typeof window === 'undefined') return;

  const [, platformName] = location.hostname.split('.').reverse();
  const { errorHandler } = app.config;

  // Add global unhandled promise rejection handler
  window.addEventListener('unhandledrejection', handleUnhandledRejection);

  // Auto-reload when Vite dynamic imports fail (stale assets after deployment).
  // Preloads also fail when an intentional cross-origin redirect aborts them —
  // reloading then would cancel the redirect and loop, so gate the reload.
  // Skip pages with unsaved long-form work (same exclusion list as app updates);
  // there the update banner already offers a manual refresh.
  window.addEventListener('vite:preloadError', (event) => {
    event.preventDefault();
    if (!isIgnoredAppUpdatePath(window.location.pathname) && canRecoveryReload()) {
      window.location.reload();
    }
  });

  if (!!config.sentry.dsn) {
    console.log('[app] Sentry enabled');

    Sentry.init({
      app,
      dsn: config.sentry.dsn,
      sendDefaultPii: true,
      environment: config.app.mode,
      attachStacktrace: true,
      trackComponents: true,
      release: `${platformName}-app@${version}`,
      integrations: [
        Sentry.browserTracingIntegration({ router }),
        // Sentry.captureConsoleIntegration({ levels: ['error', 'warn'] }),
        Sentry.contextLinesIntegration(),
        Sentry.replayIntegration({
          // Block cross-origin iframes to prevent CSS access errors
          block: ["iframe"],
        }),
      ],
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 1.0,
      tracesSampleRate: 0.3,
      // Enhanced error filtering
      ignoreErrors: [
        // Browser APIs
        /ResizeObserver/i,
        /CanceledError/i,
        /AbortError/i,
        /Network request failed/i,
        /Failed to fetch/i,
        /Load failed/i,
        
        // Status codes
        ...ignoredStatus.map(n => `code ${n}`),
        
        // Common user actions that trigger benign errors
        /User aborted/i,
        /User cancelled/i,
        /User denied/i,
        /Permission denied/i,
        
        // Third-party script errors we can't control
        /Script error/i,
        /Loading chunk/i,

        // Stale async chunks after deploy — recovered by router.onError reload
        /Couldn't resolve component/i,
        /Failed to fetch dynamically imported module/i,
        /error loading dynamically imported module/i,
        /Importing a module script failed/i,
        
        // Cross-origin CSS access errors from Sentry Replay
        /Cannot get CSS styles from text's parentNode/i,
        /CSSStyleSheet\.cssRules getter: Not allowed to access cross-origin stylesheet/i,
        
        // Navigation errors
        /Navigation cancelled/i,
        /Navigation failed/i,
        /Route cancelled/i,

        // iOS Safari IndexedDB backing store failures — query persistence degrades gracefully
        /UnknownError.*backing store/i,
        /UnknownError: Internal error/i,
      ],
      
      // Improved sampling configuration
      sampleRate: 1.0, // Capture all errors in production
      tracesSampleRate: 0.1, // Reduce performance monitoring overhead
      replaysSessionSampleRate: 0.1, // Capture 10% of sessions
      replaysOnErrorSampleRate: 1.0, // But capture all sessions that have errors
      denyUrls: [
        // Facebook flakiness
        /graph\.facebook\.com/i,
        // Facebook blocked
        /connect\.facebook\.net\/en_US\/all\.js/i,
        // Woopra flakiness
        /eatdifferent\.com\.woopra-ns\.com/i,
        /static\.woopra\.com\/js\/woopra\.js/i,
        // Chrome extensions
        /extensions\//i,
        /^chrome:\/\//i,
        /^chrome-extension:\/\//i,
        // Other plugins
        /127\.0\.0\.1:4001\/isrunning/i, // Cacaoweb
        /webappstoolbarba\.texthelp\.com\//i,
        /metrics\.itunes\.apple\.com\.edgesuite\.net\//i,
        // TODO: remove this url when fixed
        /api\/brain\/(.*)\/neurons/i
      ],
      beforeSend(event) {
        if (event.exception && event.exception.values) {
          const exception = event.exception.values[0];

          if (exception.value && exception.value.includes('Request aborted')) {
            return null;
          }

          // DOM Event captured as promise rejection — no stack, no actionable info.
          // Sentry's global onunhandledrejection integration fires before our
          // window-level handler, so we filter here as a last resort.
          if (exception.type === 'Event' && exception.value?.includes('captured as promise rejection')) {
            return null;
          }

          // Filter Vue Router navigation failures that slip through
          // (empty message in production from createRouterError)
          const value = exception.value ?? '';
          if (!value || value === 'No error message') {
            const frames = exception.stacktrace?.frames || [];
            const isRouterError = frames.some(f => {
              const fn = f.function || '';
              const mod = f.module || '';
              const file = f.filename || '';
              return fn === 'createRouterError'
                || fn === 'next'
                || mod.includes('vue-router')
                || file.includes('vue-router')
                || file.includes('vue-vendor');
            });
            if (isRouterError) return null;
          }
        }

        return event;
      },
    })

    Sentry.setTag('fingerprint', Cookie.get('fingerprint'));

    // replace the default Vue error handler from Sentry
    useErrorHandler(app, { attachProps: true, attachAttrs: true, errorHandler });
  }

})
